/**
 * RV-02 — the comments card.
 *
 * One row per comment: author · date · resolved marker, the comment text, then
 * Resolve and Delete. Acted-on rows drop to 40% opacity so the list still reads
 * as a record of what was there, rather than silently shrinking.
 *
 * The asymmetry between the two actions is the point (G-8 closure):
 *   - **Resolve** is one click. Word can reverse it from its own review pane.
 *   - **Delete** takes two. It permanently removes a colleague's comment and
 *     there is no in-pane undo, so the row asks "Delete this comment?" first.
 *
 * Comment text is rendered as a React child, never as HTML, so a comment
 * containing `<` or `&` shows those characters instead of being interpreted
 * (RV-02 AC-5).
 */

import { useCallback, useState } from "react";
import type { WordComment } from "../types";
import styles from "./WordCommentsCard.module.css";

export interface WordCommentsCardProps {
  readonly comments: readonly WordComment[];
  /** Runs `word.resolve_comment`. Resolves to true when the document changed. */
  readonly onResolve: (id: string) => Promise<boolean>;
  /** Runs `word.delete_comment`. Resolves to true when the document changed. */
  readonly onDelete: (id: string) => Promise<boolean>;
}

/** What has happened to a row this session — drives its rendering. */
type RowState = "idle" | "confirming-delete" | "busy" | "resolved" | "deleted";

/**
 * The marker appended to a row's meta line. Deletion wins over resolution: a
 * deleted comment is gone from the document, so calling it resolved would
 * describe a state the document no longer has.
 */
function statusMarker(state: RowState, isResolved: boolean): string {
  if (state === "deleted") return " · deleted";
  return isResolved ? " · resolved" : "";
}

export function WordCommentsCard({ comments, onResolve, onDelete }: WordCommentsCardProps) {
  const [rows, setRows] = useState<Record<string, RowState>>({});
  // Resolved status, tracked separately from `rows` so that opening (or
  // backing out of) the delete confirmation — which overwrites `rows[id]` —
  // can never erase a prior resolve. Previously it did: Delete then Keep
  // after a Resolve reset `rows[id]` to "idle", making the row look never
  // resolved even though the document itself still was.
  const [resolvedIds, setResolvedIds] = useState<ReadonlySet<string>>(() => new Set());

  const setRow = useCallback((id: string, next: RowState) => {
    setRows((prev) => ({ ...prev, [id]: next }));
  }, []);

  const runResolve = useCallback(
    async (id: string) => {
      setRow(id, "busy");
      const ok = await onResolve(id);
      // A failed action returns the row to idle rather than marking it done —
      // showing "resolved" for a comment Word refused to change would be a lie
      // about the document.
      if (ok) setResolvedIds((prev) => new Set(prev).add(id));
      setRow(id, ok ? "resolved" : "idle");
    },
    [onResolve, setRow],
  );

  const runDelete = useCallback(
    async (id: string) => {
      setRow(id, "busy");
      const ok = await onDelete(id);
      setRow(id, ok ? "deleted" : "idle");
    },
    [onDelete, setRow],
  );

  if (comments.length === 0) return null;

  return (
    <section
      className={styles.card}
      aria-label={`${comments.length} comment${comments.length === 1 ? "" : "s"} in this document`}
    >
      <h4 className={styles.heading}>
        {comments.length} comment{comments.length === 1 ? "" : "s"} in this document:
      </h4>

      <ul className={styles.list}>
        {comments.map((comment) => {
          const state = rows[comment.id] ?? "idle";
          // A comment already resolved in the document reads the same as one
          // resolved here — the marker describes the document, not this session.
          const isResolved = state === "resolved" || resolvedIds.has(comment.id) || comment.resolved;
          const settled = isResolved || state === "deleted";
          const busy = state === "busy";
          const rowClass = settled ? `${styles.row} ${styles.settled}` : styles.row;

          return (
            <li key={comment.id} className={rowClass} data-testid={`comment-row-${comment.id}`}>
              <p className={styles.meta}>
                <strong>{comment.author || "Unknown author"}</strong>
                {comment.date ? ` · ${comment.date}` : ""}
                {statusMarker(state, isResolved)}
              </p>

              <p className={styles.text}>{comment.text}</p>

              {state === "deleted" ? null : (
                <div className={styles.actions}>
                  {state === "confirming-delete" ? (
                    <>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.danger}`}
                        onClick={() => void runDelete(comment.id)}
                      >
                        Delete this comment?
                      </button>
                      <button
                        type="button"
                        className={styles.btn}
                        onClick={() => setRow(comment.id, "idle")}
                      >
                        Keep
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={styles.btn}
                        disabled={busy || isResolved}
                        onClick={() => void runResolve(comment.id)}
                      >
                        Resolve
                      </button>
                      <button
                        type="button"
                        className={styles.btn}
                        disabled={busy}
                        onClick={() => setRow(comment.id, "confirming-delete")}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
